import React, { useState, useEffect } from "react";
import { DatePicker, ConfigProvider, Select } from "antd";
import dayjs from "dayjs";
import Api from "../data/redux/utils/apiCall";
import { BASE_URLS } from "../data/redux/apis/urls";

export interface AttendanceFilterValues {
  wing: string;
  department: string;
  office_location_id: string;
  date_from: string;
  date_to: string;
}

interface AttendanceFilterProps {
  onFilterChange: (filters: AttendanceFilterValues) => void;
  onSearch: () => void;
  onReset: () => void;
  onDownloadCSV?: () => void;
  showDownload?: boolean;
  filterValues: AttendanceFilterValues;
  // Optional: Pass attendance data to extract wings/departments for frontend filtering
  attendanceData?: any[];
  // Conditional show/hide filters
  showWing?: boolean;
  showDepartment?: boolean;
  showLocation?: boolean;
  showDateRange?: boolean;
}

interface Wing {
  wing: string;
  wing_name: string;
}

interface Department {
  department_id: string;
  department_name: string;
}

interface OfficeLocation {
  id: number;
  name: string;
  address: string;
}

const AttendanceFilter: React.FC<AttendanceFilterProps> = ({
  onFilterChange,
  onSearch,
  onReset,
  onDownloadCSV,
  showDownload = true,
  filterValues,
  attendanceData = [],
  showWing = true,
  showDepartment = true,
  showLocation = true,
  showDateRange = true,
}) => {
  const [wings, setWings] = useState<Wing[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [allDepartments, setAllDepartments] = useState<Department[]>([]);
  const [officeLocations, setOfficeLocations] = useState<OfficeLocation[]>([]);
  const [loadingWings, setLoadingWings] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Fetch all data on component mount
  useEffect(() => {
    fetchWings();
    fetchOfficeLocations();
  }, []);

  // Extract wings and departments from attendance data as fallback
  useEffect(() => {
    if (attendanceData && attendanceData.length > 0) {
      extractDataFromAttendance(attendanceData);
    }
  }, [attendanceData]);

  // Filter departments when wing changes
  useEffect(() => {
    if (filterValues.wing && allDepartments.length > 0) {
      // Filter departments based on selected wing from allDepartments
      const filteredDepts = allDepartments.filter(
        (dept: any) => dept.wing === filterValues.wing
      );
      setDepartments(filteredDepts);
    } else if (filterValues.wing) {
      fetchDepartmentsByWing(filterValues.wing);
    } else {
      setDepartments([]);
    }
  }, [filterValues.wing, allDepartments]);

  // Extract unique wings and departments from attendance data
  const extractDataFromAttendance = (data: any[]) => {
    const uniqueWingsMap = new Map<string, Wing>();
    const uniqueDeptsMap = new Map<string, any>();

    data.forEach((item: any) => {
      const deptInfo =
        item?.employee?.details?.sap_department_designation_wings;
      if (deptInfo) {
        // Extract wing - use wing_name if wing is missing
        const wingId = deptInfo.wing || deptInfo.wing_name;
        if (wingId && !uniqueWingsMap.has(wingId)) {
          uniqueWingsMap.set(wingId, {
            wing: wingId,
            wing_name: deptInfo.wing_name || wingId,
          });
        }
        // Extract department with wing info
        if (
          deptInfo.department_id &&
          !uniqueDeptsMap.has(deptInfo.department_id)
        ) {
          uniqueDeptsMap.set(deptInfo.department_id, {
            department_id: deptInfo.department_id,
            department_name: deptInfo.department_name,
            wing: wingId,
          });
        }
      }
    });

    // Only update if we have data and current arrays are empty
    if (uniqueWingsMap.size > 0 && wings.length === 0) {
      setWings(Array.from(uniqueWingsMap.values()));
    }
    if (uniqueDeptsMap.size > 0) {
      setAllDepartments(Array.from(uniqueDeptsMap.values()));
    }
  };

  const fetchWings = async () => {
    setLoadingWings(true);
    try {
      const endPoint = `${BASE_URLS.BASE_URL}masters/find-all-departments`;
      const response = await Api.post(endPoint, {
        page: 1,
        limit: 1000,
        orderColumn: "",
        orderBy: "",
        query: "",
        filters: "",
        is_verified: "",
        date_from: "",
        is_Active: "",
        date_to: "",
        month: "",
        year: "",
      });
      console.log("Wings API Response:", response);
      
      // Handle nested data structure
      let wingsData = response?.data?.data?.data || response?.data?.data || [];
      
      if (!Array.isArray(wingsData)) {
        wingsData = [];
      }

      // Extract unique wings
      const uniqueWings = wingsData.reduce((acc: Wing[], item: any) => {
        const wingId = item.wing || item.wing_name;
        if (wingId && !acc.find((w) => w.wing === wingId)) {
          acc.push({ wing: wingId, wing_name: item.wing_name || wingId });
        }
        return acc;
      }, []);
      
      console.log("Unique Wings from API:", uniqueWings);
      if (uniqueWings.length > 0) {
        setWings(uniqueWings);
      }
    } catch (error) {
      console.error("Error fetching wings:", error);
    } finally {
      setLoadingWings(false);
    }
  };

  const fetchDepartmentsByWing = async (wing: string) => {
    setLoadingDepartments(true);
    try {
      const endPoint = `${BASE_URLS.BASE_URL}masters/find-all-departments-by-wing`;
      const response = await Api.post(endPoint, { wing });
      console.log("Departments API Response:", response);
      
      let deptData = response?.data?.data?.data || response?.data?.data || [];
      if (!Array.isArray(deptData)) {
        deptData = [];
      }
      console.log("Departments fetched:", deptData);
      if (deptData.length > 0) {
        setDepartments(deptData);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const fetchOfficeLocations = async () => {
    setLoadingLocations(true);
    try {
      const endPoint = `${BASE_URLS.BASE_URL}office-locations/find-all`;
      const response = await Api.post(endPoint, { page: 1, limit: 1000 });
      console.log("Office Locations API Response:", response);
      
      // Handle nested data structure
      let locationsData = response?.data?.data?.data || response?.data?.data || [];
      if (!Array.isArray(locationsData)) {
        locationsData = [];
      }
      console.log("Office Locations fetched:", locationsData);
      setOfficeLocations(locationsData);
    } catch (error) {
      console.error("Error fetching office locations:", error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleWingChange = (value: string) => {
    onFilterChange({
      ...filterValues,
      wing: value || "",
      department: "", // Reset department when wing changes
    });
  };

  const handleDepartmentChange = (value: string) => {
    onFilterChange({
      ...filterValues,
      department: value || "",
    });
  };

  const handleLocationChange = (value: string) => {
    onFilterChange({
      ...filterValues,
      office_location_id: value || "",
    });
  };

  const handleDateFromChange = (date: any) => {
    onFilterChange({
      ...filterValues,
      date_from: date ? dayjs(date).format("YYYY-MM-DD") : "",
    });
  };

  const handleDateToChange = (date: any) => {
    onFilterChange({
      ...filterValues,
      date_to: date ? dayjs(date).format("YYYY-MM-DD") : "",
    });
  };

  const handleReset = () => {
    onFilterChange({
      wing: "",
      department: "",
      office_location_id: "",
      date_from: "",
      date_to: "",
    });
    onReset();
  };

  const fromVal = filterValues.date_from
    ? dayjs(filterValues.date_from, "YYYY-MM-DD")
    : null;
  const toVal = filterValues.date_to
    ? dayjs(filterValues.date_to, "YYYY-MM-DD")
    : null;

  // Prepare options for Select components
  const wingOptions = wings.map((wing) => ({
    label: wing.wing_name || wing.wing,
    value: wing.wing,
  }));

  const departmentOptions = departments.map((dept) => ({
    label: dept.department_name,
    value: dept.department_id,
  }));

  const locationOptions = officeLocations.map((loc) => ({
    label: loc.name,
    value: loc.id.toString(),
  }));

  return (
    <div
      className="d-flex align-items-center gap-2 p-2 flex-wrap"
      style={{ background: "#f8f9fa", borderRadius: "8px" }}
    >
      {/* Wing Filter */}
      {showWing && (
        <div style={{ minWidth: 160 }}>
          <Select
            placeholder="Select Wing"
            style={{ width: "100%", cursor: "pointer" }}
            value={filterValues.wing || undefined}
            onChange={handleWingChange}
            allowClear
            loading={loadingWings}
            showSearch
            options={wingOptions}
            filterOption={(input, option) =>
              (option?.label as string)
                ?.toLowerCase()
                ?.includes(input.toLowerCase())
            }
          />
        </div>
      )}

      {/* Department Filter */}
      {showDepartment && (
        <div style={{ minWidth: 180 }}>
          <Select
            placeholder="Select Department"
            style={{ width: "100%", cursor: "pointer" }}
            value={filterValues.department || undefined}
            onChange={handleDepartmentChange}
            allowClear
            loading={loadingDepartments}
            disabled={!filterValues.wing}
            showSearch
            options={departmentOptions}
            filterOption={(input, option) =>
              (option?.label as string)
                ?.toLowerCase()
                ?.includes(input.toLowerCase())
            }
          />
        </div>
      )}

      {/* Assigned Location Filter */}
      {showLocation && (
        <div style={{ minWidth: 200 }}>
          <Select
            placeholder="Select Assigned Location"
            style={{ width: "100%", cursor: "pointer" }}
            value={filterValues.office_location_id || undefined}
            onChange={handleLocationChange}
            allowClear
            loading={loadingLocations}
            showSearch
            options={locationOptions}
            filterOption={(input, option) =>
              (option?.label as string)
                ?.toLowerCase()
                ?.includes(input.toLowerCase())
            }
          />
        </div>
      )}

      {/* From Date */}
      {showDateRange && (
        <div style={{ minWidth: 140 }}>
          <ConfigProvider>
            <DatePicker
              size="middle"
              style={{ width: "100%", cursor: "pointer" }}
              placeholder="From Date"
              format="DD-MM-YYYY"
              value={fromVal}
              onChange={handleDateFromChange}
            />
          </ConfigProvider>
        </div>
      )}

      {/* To Date */}
      {showDateRange && (
        <div style={{ minWidth: 140 }}>
          <ConfigProvider>
            <DatePicker
              size="middle"
              style={{ width: "100%", cursor: "pointer" }}
              placeholder="To Date"
              format="DD-MM-YYYY"
              value={toVal}
              onChange={handleDateToChange}
              disabledDate={(current) => {
                if (!fromVal) return false;
                return current && current < fromVal.startOf("day");
              }}
            />
          </ConfigProvider>
        </div>
      )}

      {/* Action Buttons */}
      <div className="d-flex gap-2">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          style={{
            height: "32px",
            lineHeight: "1",
            padding: "0.25rem 0.75rem",
          }}
          onClick={onSearch}
        >
          Apply Filter
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          style={{
            height: "32px",
            lineHeight: "1",
            padding: "0.25rem 0.75rem",
          }}
          onClick={handleReset}
        >
          Reset
        </button>
        {showDownload && onDownloadCSV && (
          <button
            type="button"
            className="btn btn-success btn-sm"
            style={{
              height: "32px",
              lineHeight: "1",
              padding: "0.25rem 0.75rem",
            }}
            onClick={onDownloadCSV}
          >
            <i className="ti ti-download me-1"></i>
            CSV
          </button>
        )}
      </div>
    </div>
  );
};

export default AttendanceFilter;
